import React, {
  CSSProperties,
  MouseEventHandler,
  ReactNode,
  useState,
} from "react";
import FlexBox from "../../Components/FlexBox/FlexBox";
import { ISelectProps } from "../../Form/Select/Select";
import Typography from "../../Foundation/Typography/Typography";
import {
  _ProductLineImageWrapper,
  _ProductLineRoot,
} from "./ProductLine.styled";
import useBreakpoint from "../../Components/Grid/hooks/useBreakpoint";
import Tag from "../../Components/Tag/Tag";
import ProductLineImage, {
  IProductLineImageProps,
} from "./ProductLineImage/ProductLineImage";
import ProductLineQuantifier from "./ProductLineQuantifier/ProductLineQuantifier";
import SpanButton from "../../General/Reset/SpanButton/SpanButton";
import Icon from "../../Foundation/Icon/Icon";

export enum EProductLineVariant {
  DEFAULT = "default",
  ORDER = "order",
}
export enum EProductLineType {
  DEFAULT = "default",
  SIDEDISH = "sidedish",
}

export interface IProductLineBaseProps {
  productLineType?: EProductLineType;
  variant?: EProductLineVariant;
  image?: IProductLineImageProps["image"];
  id_object_picture?: string;
  name: ReactNode;
  priceLabel?: string | ReactNode;
  strikethroughPrice?: string;
  price?: string;
  priceInfo?: string;
  size?: IProductLineImageProps["size"];
  subtitle?: ReactNode;
  details?: ReactNode;
  detailsOnLabel?: string;
  detailsOffLabel?: string;
  warning?: ReactNode;
  style?: CSSProperties;
  count?: number;
  isFree?: boolean;
  unAvailable?: boolean;
  isEditable?: boolean;
  onChange?: ISelectProps<number>["onChange"];
  onClick?: () => void;
  loading?: boolean;
  mentions?: string[];
}

export type OrderProductLineProps = IProductLineBaseProps & {
  variant: EProductLineVariant.ORDER;
  selectOptions?: undefined;
  max?: number;
};

export type CartProductLineProps = IProductLineBaseProps & {
  variant?: EProductLineVariant.DEFAULT;
  selectOptions: ISelectProps<number>["options"];
  max?: number;
};

export type ProductLineProps = OrderProductLineProps | CartProductLineProps;

export interface IProductLineProps extends IProductLineBaseProps {
  variant?: ProductLineProps["variant"];
  selectOptions?: ISelectProps<number>["options"];
}
const ProductLine = (props: ProductLineProps) => {
  const {
    productLineType = EProductLineType.DEFAULT,
    variant = EProductLineVariant.DEFAULT,
    id_object_picture,
    name,
    price,
    size = 72,
    style,
    subtitle,
    details = "",
    detailsOnLabel = "",
    detailsOffLabel = "",
    warning,
    count = 0,
    selectOptions,
    isFree,
    priceLabel = "",
    onChange,
    onClick,
    unAvailable = false,
    loading = false,
    isEditable = true,
    priceInfo,
    strikethroughPrice,
    max,
    mentions,
  } = props;
  const isSidedish = productLineType === EProductLineType.SIDEDISH;
  const isOrder = variant === EProductLineVariant.ORDER;
  const { isMobile } = useBreakpoint();

  const hasQuantifier =
    (isSidedish || (count > 0 && !isFree && isEditable)) && !unAvailable;

  const [detailOpen, setDetailOpen] = useState(false);
  const handleToggleDetail: MouseEventHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDetailOpen((v) => !v);
  };

  return (
    <_ProductLineRoot
      className={"ro-product-line"}
      align={"flex-start"}
      gap={16}
      style={style}
      onClick={onClick}
      $isFree={isFree}
    >
      <ProductLineImage {...{ size, id_object_picture }} />

      <FlexBox
        flex={1}
        direction="column"
        style={{ minHeight: size, overflow: "visible" }}
      >
        <FlexBox
          flex={1}
          gap={16}
          justify={"flex-start"}
          style={{
            overflow: "visible",
          }}
        >
          <FlexBox
            flex={1}
            direction="column"
            justify={"center"}
            style={{
              overflow: "visible",
            }}
          >
            <Typography
              variant="body"
              color="placeholder"
              className="product-line__name"
            >
              {name}
            </Typography>
            <div className="product-line__price-label">
              {priceLabel && (
                <>
                  <Typography as={"span"} variant="body" color="greyText">
                    {priceLabel}{" "}
                  </Typography>
                  {strikethroughPrice && (
                    <Typography
                      as={"span"}
                      variant="body"
                      color="strikethroughPrice"
                      strikethrough={!isFree}
                    >
                      {strikethroughPrice}{" "}
                    </Typography>
                  )}
                </>
              )}
              {priceInfo && (
                <Typography
                  as={"span"}
                  variant="body2"
                  color="greyText2"
                  className={"product-line__pieces"}
                >
                  {priceInfo}
                </Typography>
              )}
            </div>
            {subtitle && (
              <Typography
                className="product-line__subtitle"
                variant="body2"
                color="greyText"
              >
                {subtitle}
              </Typography>
            )}
            {warning && (
              <Typography
                className="product-line__warning"
                variant="body2"
                color="error"
              >
                {warning}
              </Typography>
            )}
            {details && detailOpen && (
              <Typography
                className="product-line__details"
                variant="body2"
                color="greyText"
                style={{ marginTop: 6, maxWidth: "15em" }}
              >
                {details}
              </Typography>
            )}
            {details && (
              <Typography
                className="product-line__details"
                variant="body2"
                color="champagne2"
                onClick={handleToggleDetail}
                style={{
                  padding: 6,
                  margin: "0px auto -8px -6px",
                  bottom: 4,
                  position: "relative",
                  cursor: "pointer",
                }}
              >
                {detailOpen ? detailsOffLabel : detailsOnLabel}
              </Typography>
            )}
            {mentions?.map((mention, i) => (
              <Typography
                key={i}
                variant="ui4"
                color="greyText"
                style={{
                  letterSpacing: "0.12px",
                }}
              >
                {mention}
              </Typography>
            ))}
          </FlexBox>

          {/* Right content */}
          <FlexBox
            className="product-line__right"
            align="center"
            style={{ maxHeight: size }}
          >
            {hasQuantifier ? (
              <ProductLineQuantifier
                {...{
                  count,
                  options: selectOptions,
                  onChange,
                  isMobile,
                  loading,
                  max,
                }}
              />
            ) : isFree ? (
              <ProductLineQuantifier
                {...{
                  count,
                  options: selectOptions,
                  onChange,
                  isMobile,
                  loading,
                  max: 1,
                }}
              />
            ) : (
              <Tag variant="order-quantity">{count}</Tag>
            )}
          </FlexBox>
        </FlexBox>
      </FlexBox>
    </_ProductLineRoot>
  );
};

export default ProductLine;
